import React, { useEffect, useState } from 'react';
import { Trash2Icon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { groupLabels, groupOrder } from '../../data/labels';
import { Frequency, TaskGroup, TaskTemplate } from '../../types/family';
import { Effective } from '../../types/shared';
import { uid } from '../../utils/db';
import { equalShares } from '../../utils/progress';
import { isWeeklyTask, partLabels, partOfDayOf, partOrder } from '../../utils/dayParts';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Chip, EffectiveChoice, Field, Toggle } from '../ui/Form';
const moduleForGroup: Record<TaskGroup, TaskTemplate['module']> = {
  rutina: 'osebnaRutina',
  sola: 'sola',
  zivali: 'zivali',
  hisa: 'hisnaOpravila',
  vecer: 'vecerniRezim'
};
type FreqKind = Frequency['kind'] | 'enkratno';
export function blankTemplate(title = '', group: TaskGroup = 'hisa'): TaskTemplate {
  return {
    id: uid('t'),
    title,
    group,
    module: moduleForGroup[group],
    confirmation: group === 'hisa' ? 'skrbnik' : 'zaupanje',
    assigneeIds: [],
    baseValue: 0,
    valueType: 'osnovno',
    transferable: group === 'hisa' || group === 'zivali',
    timed: false,
    frequency: {
      kind: 'tedensko',
      times: 1
    }
  };
}
export function TaskEditSheet({
  template,
  onClose
}: {
  template: TaskTemplate | null;
  onClose: () => void;
}) {
  const {
    family,
    activeMembers,
    saveTemplate,
    deleteTemplate
  } = useAppState();
  const [t, setT] = useState<TaskTemplate | null>(template);
  const [effective, setEffective] = useState<Effective>('naslednji-teden');
  useEffect(() => {
    setT(template);
    setEffective('naslednji-teden');
  }, [template]);
  if (!t) return <Sheet open={false} onClose={onClose} title="" children={null} data-id="element-751" data-mp="svpgvusgocfs" />;
  const exists = family.templates.some(x => x.id === t.id);
  const prev = family.templates.find(x => x.id === t.id);
  const valueChanged = exists && (prev?.baseValue !== t.baseValue || prev?.targetMinutes !== t.targetMinutes);
  const set = (patch: Partial<TaskTemplate>) => setT(cur => cur ? {
    ...cur,
    ...patch
  } : cur);
  const fk: FreqKind = t.frequency?.kind ?? 'enkratno';
  const freqN = t.frequency?.kind === 'dnevno' || t.frequency?.kind === 'tedensko' ? t.frequency.times : t.frequency?.kind === 'vsakihNDni' ? t.frequency.n : 1;
  const setFreq = (kind: FreqKind, n: number) => set({
    frequency: kind === 'enkratno' ? undefined : kind === 'dnevno' || kind === 'tedensko' ? {
      kind,
      times: Math.max(1, n)
    } : kind === 'vsakihNDni' ? {
      kind,
      n: Math.max(2, n)
    } : {
      kind
    }
  });
  const shares = equalShares(t.baseValue ?? 0, t.assigneeIds);
  const personal = Boolean(t.planKey && ['branje', 'instrument', 'sport', 'vecer', 'ucenje'].includes(t.planKey));
  return <Sheet open onClose={onClose} title={exists ? 'Uredi nalogo' : 'Nova naloga'} subtitle={`Družina ${family.name}`} footer={<div className="flex gap-2" data-id="element-753" data-mp="2jsfcb47cdeyj">
          {exists && <Button variant="secondary" aria-label="Odstrani nalogo" onClick={() => {
      deleteTemplate(t.id);
      onClose();
    }} data-id="element-754" data-mp="2jqukhmcewa4d">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-755" data-mp="29seob4yj2ao0" />
            </Button>}
          <Button className="flex-1" disabled={!t.title.trim()} onClick={() => {
      saveTemplate({
        ...t,
        title: t.title.trim(),
        module: t.planKey ? t.module : moduleForGroup[t.group]
      }, effective);
      onClose();
    }} data-id="element-756" data-mp="2eqzq38tfh2ek">
            Shrani
          </Button>
        </div>} data-id="element-752" data-mp="d34zgkc9tc6h">
      <div className="space-y-4" data-id="element-757" data-mp="2xiisbdfmocfj">
        <Field label="Naziv" data-id="element-758" data-mp="2xlwsqg4qebkc">{id => <input id={id} className="field" value={t.title} onChange={e => set({
          title: e.target.value
        })} data-id="element-759" data-mp="1i3bnjz5unig4" />}</Field>
        <Field label="Opis (neobvezno)" data-id="element-760" data-mp="1m4x3jtdokcuj">{id => <textarea id={id} rows={2} className="field" value={t.description ?? ''} onChange={e => set({
          description: e.target.value || undefined
        })} data-id="element-761" data-mp="1m5zs3unj637a" />}</Field>
        {!t.planKey && <div data-id="element-762" data-mp="1xjicudstjk7e">
            <p className="mb-1.5 text-sm font-bold" data-id="element-763" data-mp="1xj06upgctrma">Skupina</p>
            <div className="flex flex-wrap gap-2" data-id="element-764" data-mp="1sn1k16aochnz">
              {groupOrder.map(g => <Chip key={g} active={t.group === g} onClick={() => set({
            group: g
          })} data-id="element-765" data-mp="1nnyhbsi62nj6">
                  {groupLabels[g]}
                </Chip>)}
            </div>
          </div>}
        {!isWeeklyTask(t) && <div data-id="element-766" data-mp="2gcdwcf1at5ui">
            <p className="mb-1.5 text-sm font-bold" data-id="element-767" data-mp="2g9rosfeeqqca">Del dneva na zaslonu Danes</p>
            <div className="flex flex-wrap gap-2" data-id="element-768" data-mp="2bekozio9e5sn">
              {partOrder.map(p => <Chip key={p} active={partOfDayOf(t) === p} onClick={() => set({
            partOfDay: p
          })} data-id="element-769" data-mp="27amph6v0usz2">
                  {partLabels[p]}
                </Chip>)}
            </div>
          </div>}
        <div data-id="element-770" data-mp="vxu1861fwmnr">
          <p className="mb-1.5 text-sm font-bold" data-id="element-771" data-mp="37j2xdz2zfh9d">Nosilci in sodelujoči {t.assigneeIds.length === 0 && <span className="font-semibold text-muted" data-id="element-772" data-mp="37h9oed4wcky7">· nihče = sprotni dogovor</span>}</p>
          <div className="flex flex-wrap gap-2" data-id="element-773" data-mp="3biliovyptucq">
            {activeMembers.map(m => <Chip key={m.id} color={m.color} active={t.assigneeIds.includes(m.id)} onClick={() => set({
            assigneeIds: t.assigneeIds.includes(m.id) ? t.assigneeIds.filter(x => x !== m.id) : [...t.assigneeIds, m.id]
          })} data-id="element-774" data-mp="3ghoavftlk63a">
                {m.name}
                {t.assigneeIds.includes(m.id) && t.assigneeIds.length > 1 ? ` · ${shares[m.id]}` : ''}
              </Chip>)}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-775" data-mp="8369fwy5vosn">
          <Field label="Vrednost" data-id="element-776" data-mp="24wr8eo2nga1i">
            {id => <input id={id} type="number" min={0} className="field" value={t.baseValue ?? 0} onChange={e => set({
            baseValue: Math.max(0, Number(e.target.value))
          })} data-id="element-777" data-mp="24y2dcr7tznu7" />}
          </Field>
          <Field label="Vrsta vrednosti" data-id="element-778" data-mp="3d7199fqpxx3j">
            {id => <select id={id} className="field" value={t.valueType ?? 'osnovno'} onChange={e => set({
            valueType: e.target.value as 'osnovno' | 'bonus'
          })} data-id="element-779" data-mp="387ok2ocdkdxs">
                <option value="osnovno" data-id="element-780" data-mp="3m4tpcm49l8rz">Cvenkotočke (redna obveznost)</option>
                <option value="bonus" data-id="element-781" data-mp="3h6a30klrduz3">Goodies (dodatno)</option>
              </select>}
          </Field>
        </div>
        {t.assigneeIds.length > 1 && <p className="-mt-2 text-xs text-muted" data-id="element-782" data-mp="3r6j3iamsa2ka">Skupna vrednost se deli med izvajalce (začetno enako). Deleže lahko skrbnik spremeni ob potrditvi.</p>}
        <div className="grid grid-cols-2 gap-3" data-id="element-783" data-mp="8r8el6ltfz3k">
          <Field label="Ponavljanje" data-id="element-784" data-mp="4pwqj5ilblxh">
            {id => <select id={id} className="field" value={fk} onChange={e => setFreq(e.target.value as FreqKind, freqN)} data-id="element-785" data-mp="4ssmwpesi2jt">
                <option value="dnevno" data-id="element-786" data-mp="drd0jc5htm16">× dnevno</option>
                <option value="vsakihNDni" data-id="element-787" data-mp="dsxv31rt54z1">Vsakih n dni</option>
                <option value="tedensko" data-id="element-788" data-mp="xcmyp8zcmatw">× tedensko</option>
                <option value="mesecno" data-id="element-789" data-mp="xb20989wy7qr">Mesečno</option>
                <option value="dogovor" data-id="element-790" data-mp="12aoijw4lme6f">Po dogovoru</option>
                <option value="enkratno" data-id="element-791" data-mp="nez0g92jal6z">Enkratno</option>
              </select>}
          </Field>
          {(fk === 'dnevno' || fk === 'tedensko' || fk === 'vsakihNDni') && <Field label={fk === 'vsakihNDni' ? 'Število dni' : 'Kolikokrat'} data-id="element-792" data-mp="emf116ef4cl3">
              {id => <input id={id} type="number" min={1} className="field" value={freqN} onChange={e => setFreq(fk, Number(e.target.value))} data-id="element-793" data-mp="ekkc7h0100bq" />}
            </Field>}
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-794" data-mp="4m4lhf4wut2r">
          <Field label="Rok / oznaka" data-id="element-795" data-mp="9mivv3tzif3y">{id => <input id={id} className="field" value={t.dueLabel ?? ''} onChange={e => set({
            dueLabel: e.target.value || undefined
          })} placeholder="npr. zvečer" data-id="element-796" data-mp="9nu17832vux6" />}</Field>
          <Field label="Prvi / naslednji rok" data-id="element-797" data-mp="3vth3txhj5ck9">{id => <input id={id} type="date" className="field" value={t.nextDue ?? ''} onChange={e => set({
            nextDue: e.target.value || undefined
          })} data-id="element-798" data-mp="3vwmrr9efa8ee" />}</Field>
        </div>
        <Field label="Ciljne minute (neobvezno)" data-id="element-799" data-mp="b6vmds9w97vu">
          {id => <input id={id} type="number" min={0} className="field" value={t.targetMinutes ?? ''} onChange={e => set({
          targetMinutes: e.target.value ? Number(e.target.value) : undefined,
          timed: Boolean(e.target.value) || t.timed
        })} data-id="element-800" data-mp="65y29a5bq3rp" />}
        </Field>
        <div className="divide-y divide-line rounded-2xl border-[1.5px] border-line px-3" data-id="element-801" data-mp="3td077urtmukb">
          <Toggle checked={t.confirmation === 'skrbnik'} onChange={v => set({
          confirmation: v ? 'skrbnik' : 'zaupanje'
        })} label="Potrdi skrbnik" hint="Izklopljeno = na zaupanje" data-id="element-802" data-mp="3oidv1pdx0xz6" />
          <Toggle checked={t.transferable} disabled={personal} onChange={v => set({
          transferable: v
        })} label="Prenosljivo" hint={personal ? 'Osebne obveznosti (branje, vadba, šport, učenje, večer) ne more izpolniti drug član.' : 'Lahko se ponudi drugemu članu za posamezen termin.'} data-id="element-803" data-mp="3enu6u3h8yyau" />
          <Toggle checked={t.timed} onChange={v => set({
          timed: v
        })} label="Pokaži časovnik" data-id="element-804" data-mp="1p4m88624msbc" />
        </div>
        {valueChanged && <EffectiveChoice value={effective} onChange={setEffective} data-id="element-805" data-mp="1u5jqsjr1todd" />}
      </div>
    </Sheet>;
}